import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Trophy, Medal, Star, ArrowSquareOut } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Phase 245 — Public, no-auth landing page for a Best-Supplier badge.
 * Used as the share-link target so anyone can view the certificate without
 * needing a platform account. Renders the badge metadata + the 1080x1080
 * image (which is also reachable directly for OG-tag previews on LinkedIn).
 */
export default function PublicBadgePage() {
  const { badgeId } = useParams();
  const [badge, setBadge] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!badgeId) return;
    axios.get(`${API}/best-supplier/${badgeId}/public`)
      .then(({ data }) => setBadge(data))
      .catch(() => setError("This badge could not be found."));
  }, [badgeId]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-300 p-6 text-center">
        <div data-testid="public-badge-error">
          <h1 className="text-2xl font-bold mb-2">Badge unavailable</h1>
          <p className="text-sm text-slate-500">{error}</p>
        </div>
      </div>
    );
  }
  if (!badge) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="animate-spin h-8 w-8 border-2 border-amber-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  const Icon = badge.tier_rank === 1 ? Trophy : badge.tier_rank === 2 ? Medal : Star;
  const color = badge.tier_color || "#D4AF37";
  const border = badge.tier_border || "#B8860B";
  const imageUrl = `${API}/best-supplier/${badgeId}/image`;

  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col" data-testid="public-badge-page">
      <header className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
        <p className="text-sm font-bold tracking-wide font-[Chivo]">{badge.tenant_name}</p>
        <a href="/" className="text-[11px] text-slate-400 hover:text-white inline-flex items-center gap-1" data-testid="public-badge-home">
          Visit platform <ArrowSquareOut size={12} />
        </a>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-4xl w-full grid md:grid-cols-2 gap-10 items-center">
          {/* Badge image */}
          <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-white/5">
            <img
              src={imageUrl}
              alt={`${badge.tier_label} Best Supplier badge for ${badge.vendor_name}, ${badge.period_label}`}
              className="w-full h-auto block"
              data-testid="public-badge-image"
            />
          </div>

          {/* Details panel */}
          <div className="space-y-5">
            <p className="text-[11px] uppercase tracking-[0.18em] font-bold" style={{ color }}>
              {badge.tier_label} · #{badge.tier_rank} · Best Supplier
            </p>
            <h1 className="text-4xl sm:text-5xl font-extrabold font-[Chivo] leading-tight" data-testid="public-badge-vendor-name">
              {badge.vendor_name}
            </h1>
            <p className="text-base text-slate-300">
              has been awarded <span className="font-bold" style={{ color }}>{badge.tier_label} Best Supplier of the Month</span> for <span className="font-bold">{badge.period_label}</span> by <span className="font-bold">{badge.tenant_name}</span>.
            </p>

            <div className="grid grid-cols-3 gap-3 pt-2">
              <Stat label="Completions" value={badge.completed ?? "—"} />
              <Stat label="SLA Grade" value={badge.sla_grade && badge.sla_grade.toLowerCase() !== "unrated" ? badge.sla_grade : "—"} />
              <Stat label="Client Rating" value={badge.rating_avg != null ? `★ ${badge.rating_avg}/5` : "—"} />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <div
                className="h-12 w-12 rounded-full flex items-center justify-center"
                style={{ background: color, border: `2px solid ${border}` }}
              >
                <Icon size={22} weight="fill" />
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Awarded automatically each month by {badge.tenant_name} based on completed work, SLA performance, and client feedback.
              </p>
            </div>

            <div className="pt-3">
              <a
                href={imageUrl}
                download
                className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white border border-white/10 rounded-md px-3 py-1.5"
                data-testid="public-badge-download"
              >
                Download PNG
              </a>
            </div>
          </div>
        </div>
      </main>

      <footer className="px-6 py-4 border-t border-white/10 text-center text-[10px] text-slate-500">
        Hosted by {badge.tenant_name}. Badge ID: {badgeId.slice(0, 8)}
      </footer>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-white/10 px-3 py-2.5">
      <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">{label}</p>
      <p className="text-base font-bold mt-1">{value}</p>
    </div>
  );
}
